
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrentLevel } from "../utils/karma-utils";

export const useKarmaProfileData = () => {
  const [karmaPoints, setKarmaPoints] = useState<number>(0);
  const [currentLevel, setCurrentLevel] = useState<number>(0);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const { user } = useAuth();

  const fetchUserKarmaProfile = useCallback(async () => {
    if (!user) {
      setKarmaPoints(0);
      setCurrentLevel(0);
      setLoadingProfile(false);
      return { points: 0, level: 0 };
    }

    setLoadingProfile(true);
    try {
      console.log("Fetching real karma points for user:", user.id);
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('karma_points, karma_level')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching profile karma data:", profileError.message);
        setKarmaPoints(0);
        setCurrentLevel(0);
        return { points: 0, level: 0 };
      }
      
      let points = 0;
      let level = 0;

      if (profileData) {
        // Use actual karma points from database, no artificial inflation
        points = profileData.karma_points || 0;
        level = profileData.karma_level || getCurrentLevel(points);
        
        console.log("Real karma points from database:", points);
        console.log("Calculated level:", level);
        
        setKarmaPoints(points);
        setCurrentLevel(level);

        // Update level in database if it wasn't stored
        if (!profileData.karma_level && level > 0) {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ karma_level: level })
            .eq('id', user.id);
          if (updateError) {
            console.error("Error updating karma_level in profile:", updateError.message);
          }
        }
      } else {
        // No profile data found, default to 0
        console.log("No profile data found, setting karma points to 0");
        setKarmaPoints(0);
        setCurrentLevel(0);
      }
      return { points, level };
    } catch (error: any) {
      console.error("Failed to fetch user karma profile:", error.message);
      setKarmaPoints(0);
      setCurrentLevel(0);
      return { points: 0, level: 0 };
    } finally {
      setLoadingProfile(false);
    }
  }, [user]);

  return {
    karmaPoints,
    setKarmaPoints,
    currentLevel,
    setCurrentLevel,
    loadingProfile,
    fetchUserKarmaProfile,
  };
};
